import React, { useEffect, useState } from "react";

import Typography from "@mui/material/Typography";

import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import { useTheme } from "@mui/material/styles";

export default function PercentageValue(props) {
  const numberWithCommas = (number) => {
    return number.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const [arrow, setArrow] = useState(null);
  const [color, setColor] = useState("primary");

  const theme = useTheme();

  const {
    periodValuePercentage,
    periodDescription1,
    periodDescription2,
    periodVariation,
    tooltipSubTitle,
    periodInterval,
    showMoreDigits,
    usePercentage,
    alterTooltip,
    invertArrows,
    invertColors,
    periodValue,
    breakpoint,
    compact,
  } = props;

  const formatVariation = (value, variation) => {
    const decimals = showMoreDigits ? 2 : 0;
    if (!isFinite(variation)) {
        return "-"
    }
    if (value === null || isNaN(value) || variation === null || isNaN(variation)) return "-";
    else return `${Math.abs(variation.toFixed(decimals))}%`;
  };

  useEffect(() => {
    const success = theme.palette.success.main;
    const error = theme.palette.error.main;
    if (
      Math.abs(periodVariation) === 100 ||
      periodValue === 0 ||
      periodVariation === 0
    ) {
      setColor("primary");
      setArrow(null);
    } else if (periodVariation > 0) {
      setColor(invertColors ? error : success);
      setArrow(
        invertArrows ? (
          <ArrowDownwardIcon sx={{ color: invertColors ? error : success }} />
        ) : (
          <ArrowUpwardIcon sx={{ color: invertColors ? error : success }} />
        ),
      );
    } else if (periodVariation < 0) {
      setColor(invertColors ? success : error);
      setArrow(
        invertArrows ? (
          <ArrowUpwardIcon sx={{ color: invertColors ? success : error }} />
        ) : (
          <ArrowDownwardIcon sx={{ color: invertColors ? success : error }} />
        ),
      );
    } else {
      setColor("primary");
      setArrow(null);
    }
  }, [invertArrows, invertColors, theme, periodValue, periodVariation]);

  let tooltipText = "No data";
  if (
    !isNaN(periodValue) &&
    periodValue !== null &&
    !isNaN(periodValuePercentage) &&
    periodValuePercentage !== null
  ) {
    tooltipText = `${numberWithCommas(periodValue)} ${tooltipSubTitle}`;
    if (alterTooltip) {
      tooltipText = `${periodValuePercentage}% (${tooltipText})`;
    }
  }

  if (compact) {
    const caption = `${periodDescription1} ${periodDescription2}`.replace(
      /^Compared to /,
      "vs ",
    );
    // Touch screens have no hover, so a tap opens the same pop-up.
    return (
      <Tooltip
        title={
          <Typography variant="body2" textAlign="center">
            {tooltipText}
            <br />
            {periodInterval}
          </Typography>
        }
        enterTouchDelay={0}
        leaveTouchDelay={4000}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "84px 1fr",
            columnGap: 1,
            alignItems: "center",
            "& svg": { fontSize: 20 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {arrow}
            <Typography
              sx={{ fontWeight: 500, fontSize: 20, letterSpacing: "-0.5px" }}
              color={color}
            >
              {formatVariation(periodValue, periodVariation)}
            </Typography>
          </Box>
          <Typography color="textSecondary" fontSize={13} className="condensed">
            {caption}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <React.Fragment>
      <Tooltip
        title={
          <Typography variant="body2" textAlign="center">
            {tooltipText}
            <br />
            {periodInterval}
          </Typography>
        }
      >
        <Grid
          item
          sm={12}
          container
          alignItems="center"
          justifyContent="center"
          sx={{ flexWrap: "nowrap" }}
        >
          {arrow}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 29,
              letterSpacing: showMoreDigits
                ? "-2px"
                : usePercentage
                ? "-1px"
                : "-0.5px",
              fontFamily:
                showMoreDigits && breakpoint === "xl"
                  ? "Roboto Condensed"
                  : undefined,
            }}
            color={color}
          >
            {formatVariation(periodValue, periodVariation)}
          </Typography>
        </Grid>
      </Tooltip>
      <Typography
        color="textSecondary"
        fontSize={12}
        textAlign="center"
        className="condensed no-wrap"
      >
        {periodDescription1}
        <br />
        {periodDescription2}
      </Typography>
    </React.Fragment>
  );
}
